import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Modal, NotificationVariants } from '@common/components';
import { Sizes } from '@common/types';
import { BLANK_VALUE, GENETIC_COUNSELING_TRANSLATION } from '@modules/genetic-counseling/constants';
import { useAddNotification } from '@modules/notifications/store';

import PatientProfileOverviewCard from '../PatientProfileOverviewCard/PatientProfileOverviewCard';
import { PatientDeclinedCardProps } from './PatientDeclinedCard.types';
import { MainInfo } from '../PatientProfileOverviewCard/PatientProfileOverviewCard.types';
import Reopen from '../Reopen/Reopen';
import useMutationReopenAppointment from '@/modules/genetic-counseling/hooks/useMutationReopenAppointment';
import { HELIX_SUPPORT_EMAIL, PATHS } from '@/common/constants';
import { ReopenAppointmentResponseValues } from '@/modules/genetic-counseling/types/reopen-appointment';

const PatientDeclinedCard: FC<PatientDeclinedCardProps> = ({ appointmentInfo }) => {
  const { t } = useTranslation([GENETIC_COUNSELING_TRANSLATION]);
  const addNotification = useAddNotification();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const onSuccess = (data: ReopenAppointmentResponseValues): void => {
    addNotification({
      autoClose: true,
      title: t('reopen.success'),
      description: t('reopen.success_description'),
      type: NotificationVariants.SUCCESS,
    });

    handleCloseModal();
    navigate(`/${PATHS.GENETIC_COUNSELING.ROOT}/${PATHS.GENETIC_COUNSELING.PATIENT}/${data.appointmentID}`);
  };

  const onError = (): void => {
    addNotification({
      autoClose: true,
      title: t('reopen.error_title'),
      description: t('reopen.error_description', { email: HELIX_SUPPORT_EMAIL }),
      type: NotificationVariants.ERROR,
    });
    handleCloseModal();
  };

  const { mutateAsync, isLoading } = useMutationReopenAppointment(onSuccess, onError);
  const appointmentInfoData: MainInfo[] = [
    {
      id: 'declined-date',
      label: t('declined.counseling_status_card.declined_date'),
      value: appointmentInfo?.declinedDate,
    },
    {
      id: 'reason-code',
      label: t('declined.counseling_status_card.reason_code'),
      value: appointmentInfo?.reasonCode ? t(`declined.reason.${appointmentInfo?.reasonCode}`) : BLANK_VALUE,
    },
  ];

  const handleReopenReferral = (): void => {
    setIsModalOpen(true);
  };

  const actions = [];
  actions.push({
    id: 'reopen',
    label: t('reopen_patient_referral.title'),
    onClick: handleReopenReferral,
  });

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  const handleConfirm = (): void => {
    const id = appointmentInfo.id;

    void mutateAsync({ id });
  };

  return (
    <>
      <PatientProfileOverviewCard
        title={t('declined.counseling_status_card.title')}
        actions={actions}
        mainInfo={appointmentInfoData}
        notes={appointmentInfo?.notes}
      ></PatientProfileOverviewCard>
      <Modal onClose={handleCloseModal} size={Sizes.LARGE} show={isModalOpen} title={t('reopen.title')}>
        <Reopen handleCancel={handleCloseModal} handleConfirm={handleConfirm} isLoading={isLoading} />
      </Modal>
    </>
  );
};

export default PatientDeclinedCard;
